import React, { useCallback, useMemo, useState } from 'react';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { fmtMoney } from './shared/chartTheme';
import useDrill from './shared/useDrill';
import { formatClinicDate } from '../../utils/clinicTime';

// Orders against deliveries: every purchase order sent in the period, line by line against what the
// stock receipts recorded for it, and everything that did not go the way the order said: items that
// came short or not at all, more than ordered, at another price, items the supplier sent that no
// order asked for, deliveries with no order at all, and what is still to come.
//
// The server judges every line (OrderDeliveryReportService); this screen filters and prints. Orders
// sent before the period that are still waiting are included, so what is still to come is never
// missing from a printed copy.

const day = (v) => (v ? formatClinicDate(String(v).slice(0, 10), { day: 'numeric', month: 'short', year: 'numeric' }, '') : '');

/** Units in packs where they make whole packs, else in units: the server's own rule. */
export const amount = (units, upp) => {
  const n = Number(units) || 0;
  const per = Number(upp) || 1;
  if (per > 1 && n % per === 0) {
    const packs = n / per;
    return `${packs} pack${packs === 1 ? '' : 's'}`;
  }
  return `${n} unit${n === 1 ? '' : 's'}`;
};

const signedMoney = (v) => {
  const n = Number(v || 0);
  if (n === 0) return fmtMoney(0);
  return `${n > 0 ? '+' : '-'} ${fmtMoney(Math.abs(n))}`;
};

const KIND = {
  NOT_DELIVERED: { label: 'Not delivered', chip: 'bg-rose-100 text-rose-800' },
  SHORT: { label: 'Short', chip: 'bg-rose-100 text-rose-800' },
  OVER: { label: 'More than ordered', chip: 'bg-amber-100 text-amber-800' },
  PRICE: { label: 'Different price', chip: 'bg-violet-100 text-violet-800' },
  EXTRA: { label: 'Not on the order', chip: 'bg-sky-100 text-sky-800' },
  NO_ORDER: { label: 'Without an order', chip: 'bg-gray-200 text-gray-800' },
};

const FILTERS = [
  { key: 'all', label: 'Everything', kinds: null },
  { key: 'short', label: 'Short or not delivered', kinds: ['SHORT', 'NOT_DELIVERED'] },
  { key: 'over', label: 'More than ordered', kinds: ['OVER'] },
  { key: 'price', label: 'Different price', kinds: ['PRICE'] },
  { key: 'extra', label: 'Not on the order', kinds: ['EXTRA'] },
  { key: 'noOrder', label: 'Without an order', kinds: ['NO_ORDER'] },
];

const STATE = {
  WAITING: { label: 'Waiting for delivery', chip: 'bg-gray-100 text-gray-700' },
  PART_RECEIVED: { label: 'Part received', chip: 'bg-amber-100 text-amber-800' },
  RECEIVED: { label: 'Received in full', chip: 'bg-emerald-100 text-emerald-800' },
  CLOSED_SHORT: { label: 'Closed short', chip: 'bg-rose-100 text-rose-800' },
};

const kindsText = (r) => (r.kinds || []).map((k) => KIND[k]?.label || k).join(', ');
const lateText = (r) => (Number(r.late) > 0 ? `${r.late} day${Number(r.late) === 1 ? '' : 's'} late` : '');
const receivedText = (r) => `${amount(r.received, r.unitsPerPack)}${Number(r.free) > 0 ? ` (${Number(r.free) >= Number(r.received) ? 'all' : amount(r.free, r.unitsPerPack)} free)` : ''}`;

// ------------------------------------------------------------------ the tables

const ORDER_COLUMNS = [
  {
    key: 'orderNumber', label: 'Order', wrap: true,
    render: (r) => (
      <div>
        <p className="font-semibold text-gray-900 font-mono text-sm">{r.orderNumber}</p>
        <p className="text-xs text-gray-600">{r.vendorName || 'No supplier'}</p>
        {r.earlier && <p className="text-[11px] text-amber-700">Sent before the period, still waiting</p>}
      </div>
    ),
    csv: (r) => `${r.orderNumber} ${r.vendorName || ''}`.trim(),
    searchText: (r) => `${r.orderNumber} ${r.vendorName || ''}`,
  },
  {
    key: 'orderDate', label: 'Sent', wrap: true,
    render: (r) => (
      <div>
        <p className="whitespace-nowrap">{day(r.orderDate)}</p>
        {r.expectedDate && <p className="text-xs text-gray-500 whitespace-nowrap">Expected {day(r.expectedDate)}</p>}
      </div>
    ),
    csv: (r) => day(r.orderDate),
    sortValue: (r) => r.orderDate || '',
  },
  {
    key: 'status', label: 'Status', wrap: true,
    render: (r) => {
      const st = STATE[r.status] || STATE.WAITING;
      return (
        <div>
          <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${st.chip}`}>{st.label}</span>
          {Number(r.late) > 0 && <p className="text-[11px] text-rose-600 mt-0.5">{lateText(r)}</p>}
        </div>
      );
    },
    csv: (r) => `${(STATE[r.status] || STATE.WAITING).label}${Number(r.late) > 0 ? `, ${lateText(r)}` : ''}`,
  },
  {
    key: 'asOrdered', label: 'As ordered', align: 'right',
    render: (r) => <span>{r.asOrdered} of {r.lines}</span>,
    csv: (r) => `${r.asOrdered} of ${r.lines}`,
    sortValue: (r) => Number(r.asOrdered),
  },
  {
    key: 'notAsOrdered', label: 'Not as ordered', align: 'right',
    render: (r) => <span className={Number(r.notAsOrdered) > 0 ? 'font-semibold text-rose-600' : 'text-gray-500'}>{r.notAsOrdered}</span>,
    csv: (r) => r.notAsOrdered,
    sortValue: (r) => Number(r.notAsOrdered),
  },
  {
    key: 'stillDue', label: 'Still due', align: 'right',
    render: (r) => <span className={Number(r.stillDue) > 0 ? 'font-semibold text-amber-700' : 'text-gray-500'}>{r.stillDue}</span>,
    csv: (r) => r.stillDue,
    sortValue: (r) => Number(r.stillDue),
  },
  {
    key: 'orderedValue', label: 'Ordered', align: 'right',
    render: (r) => (r.orderedValue == null ? <span className="text-gray-300">—</span> : fmtMoney(r.orderedValue)),
    csv: (r) => r.orderedValue ?? '',
    sortValue: (r) => Number(r.orderedValue || 0),
  },
  {
    key: 'billed', label: 'Billed', align: 'right',
    render: (r) => <span className="font-semibold text-gray-900">{fmtMoney(r.billed)}</span>,
    csv: (r) => r.billed,
    sortValue: (r) => Number(r.billed || 0),
  },
];

const orderOf = (r) => (r.orderNumber ? `${r.orderNumber} · ${r.vendorName || ''}` : `No order · ${r.vendorName || ''}`);

const DIFFERENCE_COLUMNS = [
  {
    key: 'name', label: 'Item', wrap: true,
    render: (r) => (
      <div>
        <p className="font-semibold text-gray-900">{r.name}</p>
        <div className="flex gap-1 flex-wrap mt-0.5">
          {(r.kinds || []).map((k) => (
            <span key={k} className={`px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${KIND[k]?.chip || 'bg-gray-100 text-gray-700'}`}>{KIND[k]?.label || k}</span>
          ))}
        </div>
      </div>
    ),
    csv: (r) => `${r.name} (${kindsText(r)})`,
    searchText: (r) => `${r.name} ${r.orderNumber || ''} ${r.vendorName || ''} ${r.invoices || ''}`,
  },
  {
    key: 'orderNumber', label: 'Order', wrap: true,
    render: (r) => (
      <div>
        <p className={`text-sm ${r.orderNumber ? 'font-mono' : 'text-gray-500'}`}>{r.orderNumber || 'No order'}</p>
        <p className="text-xs text-gray-600">{r.vendorName}</p>
      </div>
    ),
    csv: orderOf,
  },
  {
    key: 'ordered', label: 'Ordered', align: 'right',
    render: (r) => (r.orderNumber && Number(r.ordered) > 0 ? amount(r.ordered, r.unitsPerPack) : <span className="text-gray-300">—</span>),
    csv: (r) => (r.orderNumber && Number(r.ordered) > 0 ? amount(r.ordered, r.unitsPerPack) : '—'),
    sortValue: (r) => Number(r.ordered),
  },
  {
    key: 'received', label: 'Received', align: 'right',
    render: (r) => receivedText(r),
    csv: receivedText,
    sortValue: (r) => Number(r.received),
  },
  {
    key: 'what', label: 'What happened', wrap: true,
    render: (r) => <span className="text-sm text-gray-800">{r.what}</span>,
    csv: (r) => r.what,
  },
  {
    key: 'invoices', label: 'Bills', wrap: true,
    render: (r) => <span className="text-xs text-gray-600">{r.invoices || '—'}</span>,
    csv: (r) => r.invoices || '—',
  },
  {
    key: 'billed', label: 'Billed', align: 'right',
    render: (r) => fmtMoney(r.billed),
    csv: (r) => r.billed,
    sortValue: (r) => Number(r.billed || 0),
  },
];

const DUE_COLUMNS = [
  {
    key: 'name', label: 'Item', wrap: true,
    render: (r) => <span className="font-semibold text-gray-900">{r.name}</span>,
    csv: (r) => r.name,
    searchText: (r) => `${r.name} ${r.orderNumber || ''} ${r.vendorName || ''}`,
  },
  {
    key: 'orderNumber', label: 'Order', wrap: true,
    render: (r) => (
      <div>
        <p className="text-sm font-mono">{r.orderNumber}</p>
        <p className="text-xs text-gray-600">{r.vendorName}</p>
      </div>
    ),
    csv: orderOf,
  },
  {
    key: 'orderDate', label: 'Sent', wrap: true,
    render: (r) => <span className="whitespace-nowrap">{day(r.orderDate)}</span>,
    csv: (r) => day(r.orderDate),
    sortValue: (r) => r.orderDate || '',
  },
  {
    key: 'ordered', label: 'Ordered', align: 'right',
    render: (r) => amount(r.ordered, r.unitsPerPack),
    csv: (r) => amount(r.ordered, r.unitsPerPack),
    sortValue: (r) => Number(r.ordered),
  },
  {
    key: 'received', label: 'Received so far', align: 'right',
    render: (r) => (Number(r.received) > 0 ? receivedText(r) : <span className="text-gray-400">None</span>),
    csv: (r) => (Number(r.received) > 0 ? receivedText(r) : 'None'),
    sortValue: (r) => Number(r.received),
  },
  {
    key: 'stillDue', label: 'Still due', align: 'right',
    render: (r) => <span className="font-semibold text-gray-900">{amount(r.stillDue, r.unitsPerPack)}</span>,
    csv: (r) => amount(r.stillDue, r.unitsPerPack),
    sortValue: (r) => Number(r.stillDue),
  },
  {
    key: 'expectedDate', label: 'Expected', wrap: true,
    render: (r) => (
      <div>
        <p className="whitespace-nowrap">{day(r.expectedDate) || '—'}</p>
        {Number(r.late) > 0 && <p className="text-[11px] font-semibold text-rose-600">{lateText(r)}</p>}
      </div>
    ),
    csv: (r) => `${day(r.expectedDate) || '—'}${Number(r.late) > 0 ? `, ${lateText(r)}` : ''}`,
    sortValue: (r) => r.expectedDate || '',
  },
];

// ------------------------------------------------------------------ the page

const OrderDeliveriesReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('last30'));
  const [preset, setPreset] = useState('last30');
  const [filter, setFilter] = useState('all');

  const fetcher = useCallback(
    () => reportsAPI.getOrderDeliveries({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);
  const k = data?.kpis || {};
  const orders = data?.orders || [];
  const stillDue = data?.stillDue || [];
  const chosen = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const differences = useMemo(
    () => (data?.notAsOrdered || []).filter((r) => !chosen.kinds || (r.kinds || []).some((x) => chosen.kinds.includes(x))),
    [data, chosen]
  );

  const unordered = Number(k.extras || 0) + Number(k.withoutOrder || 0);
  const unorderedValue = Number(k.extrasValue || 0) + Number(k.withoutOrderValue || 0);
  const effect = Number(k.priceEffect || 0);
  const kpis = [
    {
      label: 'Orders sent', value: k.orders ?? 0,
      sub: Number(k.earlierOpen) > 0 ? `+${k.earlierOpen} older still waiting` : 'In the period',
    },
    {
      label: 'As ordered', value: `${k.asOrdered ?? 0} of ${k.settled ?? 0}`,
      sub: 'Lines in full, at the order\'s price',
    },
    { label: 'Short or not delivered', value: k.short ?? 0, tone: Number(k.short) > 0 ? 'alert' : undefined },
    { label: 'More than ordered', value: k.over ?? 0, tone: Number(k.over) > 0 ? 'alert' : undefined },
    {
      label: 'Different price', value: k.priceDifferent ?? 0,
      sub: Number(k.priceDifferent) > 0 ? `${signedMoney(effect)} against the order` : undefined,
      tone: Number(k.priceDifferent) > 0 ? 'alert' : undefined,
    },
    {
      label: 'Not on any order', value: unordered,
      sub: unordered > 0 ? fmtMoney(unorderedValue) : undefined,
      tone: unordered > 0 ? 'alert' : undefined,
    },
    {
      label: 'Still to come', value: k.stillDue ?? 0,
      sub: Number(k.late) > 0 ? `${k.late} late` : undefined,
      tone: Number(k.late) > 0 ? 'alert' : undefined,
    },
  ];

  const since = data?.trackingSince;
  const startedInside = since && range.from < since;
  const openOrder = (r) => { if (r.orderId) drill.open({ type: 'orderDeliveries', orderId: r.orderId }); };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        Every order sent in the period, and any older order still waiting, against what the stock receipts
        recorded for it. A line is as ordered when what arrived equals what was ordered, at the order&apos;s price
        per pack (free units included, as the order was priced). A delivery saved without an order is matched to
        the supplier&apos;s order still waiting on its items; what matches no order is listed as not on any order.
        {since
          ? ` Ordering through the system started on ${day(since)}${startedInside ? ', so deliveries before then are not judged' : ''}.`
          : ' No order has been sent through the system yet.'}
        {' '}Click an order for every line, bill and delivery.
      </p>

      <ReportTable
        onRowClick={openOrder}
        title="Orders"
        columns={ORDER_COLUMNS}
        rows={orders}
        csvName={`orders-${range.from}-to-${range.to}`}
        emptyText={loading ? 'Loading' : 'No order was sent in the selected range, and none is waiting'}
        noun="order"
      />

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Which differences">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === f.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <ReportTable
        onRowClick={openOrder}
        title="Not as ordered"
        columns={DIFFERENCE_COLUMNS}
        rows={differences}
        csvName={`not-as-ordered-${range.from}-to-${range.to}`}
        emptyText={loading ? 'Loading' : filter === 'all' ? 'Everything delivered matched its order' : 'Nothing of this kind in the selected range'}
        noun="item"
        footnote="Short and not delivered are lines on an order closed short: the rest is not coming. A line still on its way is under Still to come, not here. Different price compares the cost per pack billed, free units included, with the order's; a gap under 1% or half a rupee a pack is rounding."
      />

      <ReportTable
        onRowClick={openOrder}
        title="Still to come"
        columns={DUE_COLUMNS}
        rows={stillDue}
        csvName={`still-to-come-${range.from}-to-${range.to}`}
        emptyText={loading ? 'Loading' : 'Nothing is waiting on a delivery'}
        noun="item"
        footnote="Lines of orders still open, whenever they were sent. Late counts from the day the order was expected."
      />

      <ReportPrintLayout
        title="Orders against Deliveries"
        subtitle={`Orders sent ${day(range.from)} to ${day(range.to)}, and older orders still waiting${filter === 'all' ? '' : ` · Showing: ${chosen.label}`}`}
        kpis={kpis.map((x) => ({ label: x.label, value: x.sub ? `${x.value} (${x.sub})` : x.value }))}
        tables={[
          { title: 'Orders', columns: ORDER_COLUMNS, rows: orders },
          { title: 'Not as ordered', columns: DIFFERENCE_COLUMNS, rows: differences },
          { title: 'Still to come', columns: DUE_COLUMNS, rows: stillDue },
        ]}
        signatures={['Prepared by', 'Checked by', 'Approved by']}
      />

      {drill.node}
    </div>
  );
};

export default OrderDeliveriesReport;
